import React, { useEffect, useState } from 'react';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import { compose } from 'redux';
import { Field, Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

// Import util modules
import { intlShape, injectIntl, FormattedMessage } from '../../../../util/reactIntl';
import { propTypes } from '../../../../util/types';
import { required } from '../../../../util/validators';

// Import shared components
import {
  Form,
  Button,
  FieldTextInput,
  MapFormField,
  // FieldDateInput,
  FieldSelect,
  FieldSingleDatePicker,
  FileUploadField,
  InfoPopup,
  IconSearch,
  ValidationError,
} from '../../../../components';

import css from './EditListingFeaturesForm.module.css';
import useListingAutosave from '../useListingAutosave';
import ListingAutosaveStatus from '../ListingAutosaveStatus';
import { anchorSystemCategories } from '../../../../config/customConfig';
import { fetchInspectors } from '../../../../util/api';

const MAX_INSPECTOR_SUGGESTIONS = 8;
const INSPECTOR_SEARCH_DELAY_MS = 250;

const isFutureInspectionDate = day => day > new Date();
const INSPECTION_DATE_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const formatInspectionDate = date =>
  date instanceof Date && !isNaN(date)
    ? `${INSPECTION_DATE_MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`
    : '';
const parseInspectionDateInput = value => {
  const match = String(value || '').match(/^([a-z]{3})\s+(\d{1,2}),\s*(\d{4})$/i);
  if (!match) {
    return null;
  }

  const [, monthValue, dayValue, yearValue] = match;
  const month = INSPECTION_DATE_MONTHS.findIndex(
    monthName => monthName.toLowerCase() === monthValue.toLowerCase()
  );
  const day = Number(dayValue);
  const year = Number(yearValue);
  const parsedDate = new Date(year, month, day);

  return parsedDate.getFullYear() === year &&
    parsedDate.getMonth() === month &&
    parsedDate.getDate() === day
    ? parsedDate
    : null;
};

const normalizeInspectorName = value =>
  String(value || '')
    .trim()
    .replace(/\s+/g, ' ');

const normalizeInspectorComparison = value => normalizeInspectorName(value).toLowerCase();

const getInspectorInitials = name => {
  const words = normalizeInspectorName(name)
    .split(' ')
    .filter(Boolean);
  const initials = words
    .slice(0, 2)
    .map(word => word[0])
    .join('');

  return initials.toUpperCase() || '+';
};

const setInspectorFormValues = (form, inspector) => {
  const normalizedName = normalizeInspectorName(inspector?.name);

  form.change('inspectorId', inspector?.id ? String(inspector.id) : null);
  form.change('inspectorName', normalizedName || null);
  form.change('inspectorSource', inspector?.source || (inspector?.id ? 'strapi' : 'custom'));
  form.change('inspectorIsPublished', Boolean(inspector?.isPublished));
  form.change('inspectorLogoUrl', inspector?.logoUrl || null);
  form.change('inspectorWebsiteUrl', inspector?.websiteUrl || null);
};

const clearInspectorFormValues = form => {
  form.change('inspectorId', null);
  form.change('inspectorName', null);
  form.change('inspectorSource', null);
  form.change('inspectorIsPublished', null);
  form.change('inspectorLogoUrl', null);
  form.change('inspectorWebsiteUrl', null);
};

// Show various error messages
const ErrorMessage = props => {
  const { fetchErrors } = props;
  const { updateListingError, createListingDraftError, showListingsError } = fetchErrors || {};
  const errorMessage = updateListingError ? (
    <FormattedMessage id="EditListingFeaturesForm.updateFailed" />
  ) : createListingDraftError ? (
    <FormattedMessage id="EditListingFeaturesForm.createListingDraftError" />
  ) : showListingsError ? (
    <FormattedMessage id="EditListingFeaturesForm.showListingFailed" />
  ) : null;

  if (errorMessage) {
    return <p className={css.error}>{errorMessage}</p>;
  }
  return null;
};

const InspectorAutocompleteInput = props => {
  const { id, input, label, meta, form, placeholder } = props;
  const [query, setQuery] = useState(normalizeInspectorName(input.value));
  const [inspectors, setInspectors] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const listboxId = `${id}-listbox`;
  const normalizedQuery = normalizeInspectorName(query);
  const normalizedComparison = normalizeInspectorComparison(normalizedQuery);
  const visibleInspectors = inspectors.slice(0, MAX_INSPECTOR_SUGGESTIONS);
  const exactMatch = inspectors.find(
    inspector => normalizeInspectorComparison(inspector?.name) === normalizedComparison
  );
  const showAddOption = Boolean(normalizedQuery) && !exactMatch;
  const optionCount = visibleInspectors.length + (showAddOption ? 1 : 0);

  useEffect(() => {
    setQuery(normalizeInspectorName(input.value));
  }, [input.value]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    let isCancelled = false;
    const timeoutId = window.setTimeout(() => {
      setIsLoading(true);
      fetchInspectors({ query: normalizedQuery })
        .then(response => {
          if (isCancelled) {
            return;
          }
          setInspectors(Array.isArray(response?.inspectors) ? response.inspectors : []);
          setHighlightedIndex(0);
        })
        .catch(() => {
          if (isCancelled) {
            return;
          }
          setInspectors([]);
          setHighlightedIndex(0);
        })
        .finally(() => {
          if (!isCancelled) {
            setIsLoading(false);
          }
        });
    }, INSPECTOR_SEARCH_DELAY_MS);

    return () => {
      isCancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [isOpen, normalizedQuery]);

  const selectInspector = inspector => {
    const normalizedName = normalizeInspectorName(inspector?.name);
    if (!normalizedName) {
      return;
    }

    input.onChange(normalizedName);
    input.onBlur();
    setQuery(normalizedName);
    setInspectorFormValues(form, {
      ...inspector,
      name: normalizedName,
      source: 'strapi',
    });
    setIsOpen(false);
    setHighlightedIndex(0);
  };

  const selectNewInspector = () => {
    if (!normalizedQuery) {
      return;
    }

    input.onChange(normalizedQuery);
    input.onBlur();
    setQuery(normalizedQuery);
    setInspectorFormValues(form, {
      name: normalizedQuery,
      source: 'custom',
      isPublished: false,
    });
    setIsOpen(false);
    setHighlightedIndex(0);
  };

  const handleChange = event => {
    const nextQuery = event.target.value;
    const nextName = normalizeInspectorName(nextQuery);

    input.onChange(nextQuery);
    setQuery(nextQuery);
    setIsOpen(true);
    setHighlightedIndex(0);

    if (nextName) {
      form.change('inspectorId', null);
      form.change('inspectorSource', 'custom');
      form.change('inspectorIsPublished', false);
      form.change('inspectorLogoUrl', null);
      form.change('inspectorWebsiteUrl', null);
    } else {
      clearInspectorFormValues(form);
    }
  };

  const handleBlur = () => {
    input.onBlur();
    setIsOpen(false);
    setQuery(normalizeInspectorName(input.value));
  };

  const selectHighlightedOption = () => {
    if (!isOpen || optionCount === 0) {
      return false;
    }

    const selectedInspector = visibleInspectors[highlightedIndex];
    if (selectedInspector) {
      selectInspector(selectedInspector);
      return true;
    }

    if (showAddOption && highlightedIndex === visibleInspectors.length) {
      selectNewInspector();
      return true;
    }

    return false;
  };

  const handleKeyDown = event => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIsOpen(true);
      setHighlightedIndex(currentIndex =>
        optionCount ? Math.min(currentIndex + 1, optionCount - 1) : 0
      );
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setIsOpen(true);
      setHighlightedIndex(currentIndex => Math.max(currentIndex - 1, 0));
    } else if (event.key === 'Enter' && selectHighlightedOption()) {
      event.preventDefault();
    } else if (event.key === 'Escape') {
      setIsOpen(false);
      setQuery(normalizeInspectorName(input.value));
    }
  };

  const hasError = !!(meta.touched && meta.invalid && meta.error);
  const fieldMeta = { touched: hasError, error: meta.error };
  const showSuggestions = isOpen && optionCount > 0;
  const activeDescendant = showSuggestions ? `${listboxId}-option-${highlightedIndex}` : undefined;

  return (
    <div className={css.inspectorAutocomplete}>
      <label htmlFor={id}>{label}</label>
      <div className={css.inspectorAutocompleteInputWrap}>
        <IconSearch className={css.inspectorSearchIcon} />
        <input
          {...input}
          id={id}
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-activedescendant={activeDescendant}
          autoComplete="off"
          className={classNames(css.inspectorAutocompleteInput, {
            [css.inspectorAutocompleteInputError]: hasError,
          })}
          placeholder={placeholder}
          value={query}
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
        />
      </div>
      {showSuggestions ? (
        <ul id={listboxId} role="listbox" className={css.inspectorSuggestions}>
          {visibleInspectors.map((inspector, index) => {
            const isHighlighted = highlightedIndex === index;

            return (
              <li
                id={`${listboxId}-option-${index}`}
                key={inspector.id || inspector.slug || inspector.name}
                role="option"
                aria-selected={isHighlighted}
                className={classNames(css.inspectorSuggestion, {
                  [css.inspectorSuggestionHighlighted]: isHighlighted,
                })}
                onMouseDown={event => {
                  event.preventDefault();
                  selectInspector(inspector);
                }}
              >
                <span className={css.inspectorAvatar}>{getInspectorInitials(inspector?.name)}</span>
                <span className={css.inspectorSuggestionText}>
                  <span className={css.inspectorSuggestionName}>{inspector.name}</span>
                  <span className={css.inspectorSuggestionMeta}>
                    {inspector.isPublished ? (
                      <FormattedMessage id="EditListingFeaturesForm.inspectorPublishedMeta" />
                    ) : (
                      <FormattedMessage id="EditListingFeaturesForm.inspectorDraftMeta" />
                    )}
                  </span>
                </span>
              </li>
            );
          })}
          {showAddOption ? (
            <li
              id={`${listboxId}-option-${visibleInspectors.length}`}
              role="option"
              aria-selected={highlightedIndex === visibleInspectors.length}
              className={classNames(css.inspectorSuggestion, css.inspectorAddSuggestion, {
                [css.inspectorSuggestionHighlighted]: highlightedIndex === visibleInspectors.length,
              })}
              onMouseDown={event => {
                event.preventDefault();
                selectNewInspector();
              }}
            >
              <span className={css.inspectorAddIcon}>+</span>
              <span>
                <FormattedMessage
                  id="EditListingFeaturesForm.inspectorAddOption"
                  values={{ name: normalizedQuery }}
                />
              </span>
            </li>
          ) : null}
        </ul>
      ) : null}
      {isLoading && isOpen && !showSuggestions ? (
        <div className={css.inspectorSearchStatus}>
          <FormattedMessage id="EditListingFeaturesForm.inspectorSearching" />
        </div>
      ) : null}
      <ValidationError fieldMeta={fieldMeta} />
    </div>
  );
};

const FieldInspectorAutocomplete = props => {
  return <Field component={InspectorAutocompleteInput} {...props} />;
};

// Form that asks title, description, transaction process and unit type for pricing
// In addition, it asks about custom fields according to marketplace-custom-config.js
const EditListingFeaturesFormComponent = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    render={formRenderProps => {
      const {
        className,
        disabled,
        ready,
        handleSubmit,
        intl,
        invalid,
        pristine,
        saveActionMsg,
        updated,
        updateInProgress,
        fetchErrors,
        values,
        form,
      } = formRenderProps;

      const hasInspectionReport = values?.inspectionReport;
      const savedInspectionReportName = hasInspectionReport
        ? hasInspectionReport?.split('/')?.pop()
        : null;
      const [objectURL, setObjectURL] = useState(hasInspectionReport || null);
      const [objectName, setObjectName] = useState(savedInspectionReportName);
      const [inspectionUploadInProgress, setInspectionUploadInProgress] = useState(false);

      const classes = classNames(css.root, className);
      const submitReady = (updated && pristine) || ready;
      const autosave = useListingAutosave({
        values,
        dirty: !pristine,
        valid: !invalid,
        onSave: autosaveValues => props.onSubmit(autosaveValues, { isAutosave: true }),
        enabled: !!props.autosaveEnabled,
        blocked: inspectionUploadInProgress,
      });
      const submitInProgress = updateInProgress || autosave.isSaving;
      const submitDisabled = invalid || disabled || submitInProgress || inspectionUploadInProgress;

      return (
        <Form className={classes} onSubmit={autosave.handleManualSubmit(handleSubmit)}>
          <ErrorMessage fetchErrors={fetchErrors} />
          <div className={classNames(css.formRow, css.fullWidth)}>
            <div className={css.formFld}>
              <FieldTextInput
                id="maxBoatSize"
                name="maxBoatSize"
                className={css.textField}
                type="number"
                label={intl.formatMessage({ id: 'EditListingFeaturesForm.maxBoatSize' })}
                placeholder={intl.formatMessage({
                  id: 'EditListingFeaturesForm.maxBoatSizePlaceholder',
                })}
                validate={required(
                  intl.formatMessage({
                    id: 'EditListingFeaturesForm.maxBoatSizeRequired',
                  })
                )}
              />
            </div>
          </div>
          <div className={classNames(css.formRow, css.fullWidth)}>
            <div className={css.formFld}>
              <FieldTextInput
                id="waterDepth"
                name="waterDepth"
                className={css.textField}
                type="number"
                label={
                  <>
                    {intl.formatMessage({ id: 'EditListingFeaturesForm.waterDepth' })}
                    {
                      <InfoPopup
                        info={intl.formatMessage({
                          id: 'EditListingFeaturesForm.waterDepthExtraInfo',
                        })}
                      />
                    }
                  </>
                }
                placeholder={intl.formatMessage({
                  id: 'EditListingFeaturesForm.waterDepthPlaceholder',
                })}
                validate={required(
                  intl.formatMessage({
                    id: 'EditListingFeaturesForm.waterDepthRequired',
                  })
                )}
              />
            </div>
          </div>

          <div className={classNames(css.formRow, css.fullWidth)}>
            <div className={css.formFld}>
              <FieldTextInput
                id="registration"
                name="registration"
                className={css.textField}
                type="text"
                label={intl.formatMessage({ id: 'EditListingFeaturesForm.registration' })}
              />
            </div>
          </div>
          <div className={classNames(css.formRow, css.fullWidth)}>
            <div className={css.formFld}>
              <FieldSingleDatePicker
                id="lastInspection"
                name="lastInspection"
                label={intl.formatMessage({ id: 'EditListingFeaturesForm.lastInspection' })}
                placeholderText={intl.formatMessage({
                  id: 'EditListingFeaturesForm.lastInspectionPlaceholder',
                })}
                formatDate={formatInspectionDate}
                isOutsideRange={isFutureInspectionDate}
                parseDateInput={parseInspectionDateInput}
                popupClassName={css.inspectionDatePopup}
                showClearButton
                showErrorMessage={false}
              />
            </div>
          </div>
          <div className={classNames(css.formRow, css.fullWidth)}>
            <div className={css.formFld}>
              <FieldInspectorAutocomplete
                id="inspectorName"
                name="inspectorName"
                form={form}
                label={intl.formatMessage({ id: 'EditListingFeaturesForm.inspectorLabel' })}
                placeholder={intl.formatMessage({
                  id: 'EditListingFeaturesForm.inspectorPlaceholder',
                })}
              />
            </div>
          </div>
          <div className={classNames(css.formRow, css.fullWidth)}>
            <div className={css.formFld}>
              <FieldSelect
                className={css.category}
                name="anchorSystemCategories"
                id="anchorSystemCategories"
                label={intl.formatMessage({ id: 'EditListingFeaturesForm.anchorSystemCategories' })}
                validate={required(
                  intl.formatMessage({
                    id: 'EditListingFeaturesForm.anchorSystemCategoriesRequired',
                  })
                )}
              >
                {anchorSystemCategories.map(c => (
                  <option key={c.key} value={c.key} disabled={c?.disabled}>
                    {c.label}
                  </option>
                ))}
              </FieldSelect>
            </div>
          </div>
          <div className={classNames(css.formRow, css.fullWidth)}>
            <div className={css.formFld}>
              <FileUploadField
                className={css.dynamicField}
                form={form}
                objectURL={objectURL}
                objectName={objectName}
                setObjectURL={url => {
                  setObjectURL(url);
                  form.change('inspectionReport', url);
                }}
                setObjectName={setObjectName}
                onUploadStateChange={setInspectionUploadInProgress}
                id="inspection"
                name="inspection"
                fieldLabel={intl.formatMessage({ id: 'EditListingFeaturesForm.inspectionReport' })}
              />
            </div>
          </div>
          {props.autosaveEnabled ? (
            <ListingAutosaveStatus status={autosave.status} onRetry={autosave.retry} />
          ) : null}
          <Button
            className={css.submitButton}
            type="submit"
            inProgress={submitInProgress}
            disabled={submitDisabled}
            ready={submitReady}
          >
            {saveActionMsg}
          </Button>
        </Form>
      );
    }}
  />
);

EditListingFeaturesFormComponent.defaultProps = {
  className: null,
  formId: 'EditListingFeaturesForm',
  fetchErrors: null,
  hasExistingListingType: false,
  listingFieldsConfig: [],
};

EditListingFeaturesFormComponent.propTypes = {
  className: string,
  formId: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  onListingTypeChange: func.isRequired,
  saveActionMsg: string.isRequired,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
  fetchErrors: shape({
    createListingDraftError: propTypes.error,
    showListingsError: propTypes.error,
    updateListingError: propTypes.error,
  }),
  selectableListingTypes: arrayOf(
    shape({
      listingType: string.isRequired,
      transactionProcessAlias: string.isRequired,
      unitType: string.isRequired,
    })
  ).isRequired,
  hasExistingListingType: bool,
  listingFieldsConfig: propTypes.listingFields,
};

export default compose(injectIntl)(EditListingFeaturesFormComponent);
